import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  Settings, Save, RefreshCw, AlertCircle, CheckCircle, 
  Globe, Shield, Database, Mail, Palette, Users
} from 'lucide-react';

const PortalSettings = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState({
    global: {
      portalEnabled: true,
      allowUserRegistration: false,
      sessionTimeout: 60,
      maxFileUploadSize: 50,
      storageQuotaPerBU: 1024
    },
    security: {
      enforcePasswordPolicy: true,
      minPasswordLength: 8,
      requireTwoFactor: false,
      allowedFileTypes: '.pdf,.doc,.docx,.txt,.jpg,.png,.zip',
      maxLoginAttempts: 5
    },
    email: {
      smtpEnabled: false,
      smtpHost: '',
      smtpPort: 587,
      smtpUsername: '',
      smtpPassword: '',
      fromEmail: '',
      ticketNotifications: true
    },
    appearance: {
      portalTitle: 'Client Portal',
      primaryColor: '#3B82F6',
      logoUrl: '',
      customCss: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('global');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/client-portal/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/client-portal/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save settings. Changes will be applied when backend is configured.' });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const tabs = [
    { id: 'global', name: 'Global', icon: <Globe className="w-4 h-4" /> },
    { id: 'security', name: 'Security', icon: <Shield className="w-4 h-4" /> },
    { id: 'email', name: 'Email', icon: <Mail className="w-4 h-4" /> },
    { id: 'appearance', name: 'Appearance', icon: <Palette className="w-4 h-4" /> }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="h-96 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Portal Settings</h1>
          <p className="text-slate-400 mt-1">Configure client portal system settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-green-900/30 border border-green-800 text-green-400'
            : 'bg-red-900/30 border border-red-800 text-red-400'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <div className="bg-slate-800 rounded-lg border border-slate-700">
        {/* Tabs */}
        <div className="border-b border-slate-700">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-300'
                }`}
              >
                {tab.icon}
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Global Settings */}
          {activeTab === 'global' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center justify-between">
                    <span className="text-slate-300 font-medium">Enable Client Portal</span>
                    <input
                      type="checkbox"
                      checked={settings.global.portalEnabled}
                      onChange={(e) => updateSetting('global', 'portalEnabled', e.target.checked)}
                      className="toggle toggle-primary"
                    />
                  </label>
                  <p className="text-slate-400 text-sm mt-1">Allow clients to access their portals</p>
                </div>

                <div>
                  <label className="flex items-center justify-between">
                    <span className="text-slate-300 font-medium">Allow User Registration</span>
                    <input
                      type="checkbox"
                      checked={settings.global.allowUserRegistration}
                      onChange={(e) => updateSetting('global', 'allowUserRegistration', e.target.checked)}
                      className="toggle toggle-primary"
                    />
                  </label>
                  <p className="text-slate-400 text-sm mt-1">Let users register their own accounts</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.global.sessionTimeout}
                    onChange={(e) => updateSetting('global', 'sessionTimeout', parseInt(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    min="5"
                    max="480"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Max File Upload (MB)
                  </label>
                  <input
                    type="number"
                    value={settings.global.maxFileUploadSize}
                    onChange={(e) => updateSetting('global', 'maxFileUploadSize', parseInt(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    min="1"
                    max="100"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Storage Quota per BU (MB)
                  </label>
                  <input
                    type="number"
                    value={settings.global.storageQuotaPerBU}
                    onChange={(e) => updateSetting('global', 'storageQuotaPerBU', parseInt(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    min="100"
                    max="10240"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center justify-between">
                    <span className="text-slate-300 font-medium">Enforce Password Policy</span>
                    <input
                      type="checkbox"
                      checked={settings.security.enforcePasswordPolicy}
                      onChange={(e) => updateSetting('security', 'enforcePasswordPolicy', e.target.checked)}
                      className="toggle toggle-primary"
                    />
                  </label>
                  <p className="text-slate-400 text-sm mt-1">Require strong passwords</p>
                </div>

                <div>
                  <label className="flex items-center justify-between">
                    <span className="text-slate-300 font-medium">Require Two-Factor Auth</span>
                    <input
                      type="checkbox"
                      checked={settings.security.requireTwoFactor}
                      onChange={(e) => updateSetting('security', 'requireTwoFactor', e.target.checked)}
                      className="toggle toggle-primary"
                    />
                  </label>
                  <p className="text-slate-400 text-sm mt-1">Mandatory 2FA for all users</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Minimum Password Length
                  </label>
                  <input
                    type="number"
                    value={settings.security.minPasswordLength}
                    onChange={(e) => updateSetting('security', 'minPasswordLength', parseInt(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    min="6"
                    max="20"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Max Login Attempts
                  </label>
                  <input
                    type="number"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) => updateSetting('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    min="3"
                    max="10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">
                  Allowed File Types
                </label>
                <input
                  type="text"
                  value={settings.security.allowedFileTypes}
                  onChange={(e) => updateSetting('security', 'allowedFileTypes', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder=".pdf,.doc,.docx,.txt,.jpg,.png"
                />
                <p className="text-slate-400 text-sm mt-1">Comma-separated list of allowed file extensions</p>
              </div>
            </div>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div>
                <label className="flex items-center justify-between mb-4">
                  <span className="text-slate-300 font-medium">Enable SMTP</span>
                  <input
                    type="checkbox"
                    checked={settings.email.smtpEnabled}
                    onChange={(e) => updateSetting('email', 'smtpEnabled', e.target.checked)}
                    className="toggle toggle-primary"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    value={settings.email.smtpHost}
                    onChange={(e) => updateSetting('email', 'smtpHost', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    placeholder="smtp.gmail.com"
                    disabled={!settings.email.smtpEnabled}
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    value={settings.email.smtpPort}
                    onChange={(e) => updateSetting('email', 'smtpPort', parseInt(e.target.value))}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    disabled={!settings.email.smtpEnabled}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    SMTP Username
                  </label>
                  <input
                    type="text"
                    value={settings.email.smtpUsername}
                    onChange={(e) => updateSetting('email', 'smtpUsername', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    disabled={!settings.email.smtpEnabled}
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    SMTP Password
                  </label>
                  <input
                    type="password"
                    value={settings.email.smtpPassword}
                    onChange={(e) => updateSetting('email', 'smtpPassword', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    disabled={!settings.email.smtpEnabled}
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">
                  From Email Address
                </label>
                <input
                  type="email"
                  value={settings.email.fromEmail}
                  onChange={(e) => updateSetting('email', 'fromEmail', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="noreply@company.com"
                  disabled={!settings.email.smtpEnabled}
                />
              </div>

              <div>
                <label className="flex items-center justify-between">
                  <span className="text-slate-300 font-medium">Send Ticket Notifications</span>
                  <input
                    type="checkbox"
                    checked={settings.email.ticketNotifications}
                    onChange={(e) => updateSetting('email', 'ticketNotifications', e.target.checked)}
                    className="toggle toggle-primary"
                  />
                </label>
                <p className="text-slate-400 text-sm mt-1">Send email notifications for ticket updates</p>
              </div>
            </div>
          )}

          {/* Appearance Settings */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <label className="block text-slate-300 font-medium mb-2">
                  Portal Title
                </label>
                <input
                  type="text"
                  value={settings.appearance.portalTitle}
                  onChange={(e) => updateSetting('appearance', 'portalTitle', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="Client Portal"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Primary Color
                  </label>
                  <input
                    type="color"
                    value={settings.appearance.primaryColor}
                    onChange={(e) => updateSetting('appearance', 'primaryColor', e.target.value)}
                    className="w-full h-10 bg-slate-700 border border-slate-600 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 font-medium mb-2">
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={settings.appearance.logoUrl}
                    onChange={(e) => updateSetting('appearance', 'logoUrl', e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                    placeholder="https://example.com/logo.png"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-2">
                  Custom CSS
                </label>
                <textarea
                  value={settings.appearance.customCss}
                  onChange={(e) => updateSetting('appearance', 'customCss', e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 h-32 font-mono text-sm"
                  placeholder="/* Add custom CSS styles here */"
                />
                <p className="text-slate-400 text-sm mt-1">Custom CSS will be applied to all client portals</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalSettings;